// appController's shop-window flow: selecting and leaving a shop window, and
// Pixie's video pause, resume toast and portrait lock that go with it.
import * as analytics from "@/modules/analytics";
import { shopWindowTypes, valueNames } from "@/modules/Constants";
import { isImmersiveLandscapeSize } from "@/modules/orientationManager";
import { isMcpDesktopLayout, isMcpLandscapeLayout } from "@/modules/mcpSurface";
import {
  isOverlayPlaying,
  pauseOverlayPlayback,
  resumeOverlayPlayback,
} from "@/modules/overlayPlayback";
import {
  formatClock,
  lockPortraitForPixie,
  markPixieTipSeen,
  releasePixiePortraitLock,
  showPixieToast,
} from "@/modules/pixieWorkspace";
import AppActions from "@/modules/appActions";
import type ShopWindowConfig from "@/plugin/ShopWindowConfig";

// A shop window config or nothing; read from applicationData values.
export type ShopWindowValue = ShopWindowConfig | null | undefined;

export default class AppShopWindow extends AppActions {
  declare _chatAutoPaused: boolean;

  setShopWindow(region: string, shopWindowConfig: ShopWindowValue) {
    // Dedicated per-tab Mixpanel event for the immersive landscape
    // right-side tabs (LandscapeNav, region "Landscape Nav") — each tab surfaces
    // as its own named event, e.g. "Landscape Ingredients Tab Clicked", for clean
    // per-tab dashboards. Fired here (on the SET_SHOP_WINDOW handler, before
    // setApplicationValue de-dupes an unchanged config) so it counts EVERY tap,
    // including re-tapping the already-open tab. This handler is reached only from
    // real tab taps; the generic "Set Shop Window" event still fires downstream.
    // Portrait/desktop tab bars use a different region and are unaffected.
    if (region === "Landscape Nav" && shopWindowConfig) {
      analytics.track(
        `Landscape ${shopWindowConfig.title || shopWindowConfig.name} Tab Clicked`,
        {
          pix_offset: this.state.experienceData.nowTime,
          pix_sw: shopWindowConfig.name,
        },
      );
    }
    if (shopWindowConfig && shopWindowConfig.isPortraitOverlay === false) {
      this.setApplicationValue(
        region,
        valueNames.SHOP_WINDOW_DEFAULT_CONFIG,
        shopWindowConfig,
      );
    }
    this.setApplicationValue(
      region,
      valueNames.SHOP_WINDOW_CONFIG,
      shopWindowConfig,
    );

    this._syncChatAutoPause(shopWindowConfig);
    this._syncPixieOrientation(shopWindowConfig);

    // Landscape has no inline shop-window panel: selecting a tab (from
    // LandscapeNav or the sheet's own tab bar) must also reveal the overlay
    // sheet. Unconditional, regardless of the region string the caller used
    // and regardless of whether the config value itself changed above
    // (setApplicationValue no-ops on an unchanged value, but re-selecting the
    // already-active tab must still reopen a closed sheet).
    if (this.state.applicationData.isLandscape()) {
      this.openShopWindow(region);
    }
  }

  // Auto-pause the video whenever Pixie (the chat) is the open shop window, on
  // EVERY surface -- portrait overlay, landscape sheet and desktop alike.
  // Opening Pixie is a deliberate switch of attention away from the video, so
  // the minutes behind it are not watch time regardless of layout. Resume
  // happens on close / when another window opens.
  _syncChatAutoPause(shopWindowConfig: ShopWindowValue) {
    const isChat =
      !!shopWindowConfig && shopWindowConfig.type === shopWindowTypes.ASK;
    if (isChat) {
      // Only pause a video that is actually playing, so a viewer who had
      // deliberately paused is never resumed on close.
      if (!this._chatAutoPaused && isOverlayPlaying()) {
        this._chatAutoPaused = true;
        pauseOverlayPlayback();
      }
    } else {
      // Switched to another window (or out of the chat): resume what we paused.
      this._resumeChatAutoPausedVideo();
    }
  }

  // Pixie gets her own time on a phone: opening her from immersive landscape
  // turns an Android phone to portrait (iOS keeps landscape, see pixieWorkspace).
  _syncPixieOrientation(shopWindowConfig: ShopWindowValue) {
    const isChat =
      !!shopWindowConfig && shopWindowConfig.type === shopWindowTypes.ASK;
    if (!isChat) {
      releasePixiePortraitLock();
      return;
    }
    markPixieTipSeen();
    if (
      this.state.applicationData.isLandscape() &&
      isImmersiveLandscapeSize()
    ) {
      lockPortraitForPixie();
    }
  }

  // Resume the video the portrait chat overlay auto-paused, once.
  // Shared by close (onShopWindowExit) and switching to another window
  // (_syncChatAutoPause) so the reset logic can't drift between the two.
  _resumeChatAutoPausedVideo() {
    if (this._chatAutoPaused) {
      this._chatAutoPaused = false;
      resumeOverlayPlayback();
      this._toastResumed();
    }
  }

  // Only mid-video: a video played from the start or replayed resumes nothing.
  _toastResumed() {
    const nowTime = this.state.experienceData.nowTime;
    const ended = this.state.applicationData.getValue(
      valueNames.VIDEO_ENDED,
      false,
    );
    if ((nowTime as number) > 0 && !ended) {
      showPixieToast(`Resumed at ${formatClock(nowTime)}`);
    }
  }

  // Pixie's Resume: leave Pixie and play from the second the video stopped,
  // also when the viewer had paused it before opening Pixie.
  resumeFromPixie(region: string) {
    const wasAutoPaused = this._chatAutoPaused;
    this.onShopWindowExit(
      region,
      this.state.applicationData.getValue(
        valueNames.SHOP_WINDOW_CONFIG,
      ) as ShopWindowValue,
    );
    if (!wasAutoPaused) {
      resumeOverlayPlayback();
      this._toastResumed();
    }
  }

  onShopWindowExit(region: string, shopWindowConfig: ShopWindowValue) {
    const applicationData = this.state.applicationData;
    const current = applicationData.getValue(
      valueNames.SHOP_WINDOW_CONFIG,
    ) as ShopWindowValue;
    // Leaving the desktop Workspace returns to the recipe panel, which stays open.
    const leavingDesktopWorkspace =
      !!current &&
      current.type === shopWindowTypes.ASK &&
      !isImmersiveLandscapeSize() &&
      !isMcpDesktopLayout() &&
      !isMcpLandscapeLayout();
    const config = applicationData.getValue(
      valueNames.SHOP_WINDOW_DEFAULT_CONFIG,
    );
    this.setApplicationValue(region, valueNames.SHOP_WINDOW_CONFIG, config);

    // Closing Pixie resumes the video we auto-paused (any surface).
    this._resumeChatAutoPausedVideo();
    releasePixiePortraitLock();

    // The landscape overlay sheet is driven by showShopWindow(), separately
    // from which tab's config is active — closing it (✕, scrim, or swipe)
    // must flip that flag off too.
    if (applicationData.isLandscape() && !leavingDesktopWorkspace) {
      this.closeShopWindow(region);
    }
  }
}
